import type { ChartConfig } from '@/shared/components/ui/chart'

import { useTranslation } from 'react-i18next'
import { Bar, BarChart, LabelList, XAxis, YAxis } from 'recharts'

import {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
} from '@/shared/components/ui/chart'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

export type BarSegment = {
	key: string
	label: string
	value: number
	percent: number
	color: string
}

const MIN_LABEL_WIDTH = 24
const TARGET_TICKS = 5

/** Round (1/2/2.5/5 × 10ⁿ), equally spaced ticks from 0 up to `total`. */
export const getAxisTicks = (total: number): number[] => {
	if (total <= 0) return [0]
	const raw = total / TARGET_TICKS
	const pow = 10 ** Math.floor(Math.log10(raw))
	const mantissa = raw / pow
	const step =
		(mantissa <= 1
			? 1
			: mantissa <= 2
				? 2
				: mantissa <= 2.5
					? 2.5
					: mantissa <= 5
						? 5
						: 10) * pow

	return Array.from(
		{ length: Math.floor(total / step) + 1 },
		(_, i) => i * step
	)
}

const SegmentLabel = ({
	x = 0,
	y = 0,
	width = 0,
	height = 0,
	text,
}: {
	x?: number
	y?: number
	width?: number
	height?: number
	text: string
}) =>
	width > MIN_LABEL_WIDTH ? (
		<text
			x={x + width / 2}
			y={y + height / 2}
			fill='white'
			fontSize={11}
			textAnchor='middle'
			dominantBaseline='central'
		>
			{text}
		</text>
	) : null

/** Single horizontal 100%-width bar split into colored segments. */
export const SegmentedBarChart = ({
	segments,
	showLegend = false,
	height = 70,
	'data-testid': testId,
}: {
	segments: BarSegment[]
	showLegend?: boolean
	height?: number
	'data-testid'?: string
}) => {
	const { t } = useTranslation()
	const config = Object.fromEntries(
		segments.map(({ key, label, color }) => [key, { label, color }])
	) satisfies ChartConfig
	const row = Object.fromEntries(segments.map(({ key, value }) => [key, value]))
	const total = segments.reduce((acc, { value }) => acc + value, 0)
	const byKey = Object.fromEntries(segments.map((s) => [s.key, s]))

	return (
		<ChartContainer
			config={config}
			className='aspect-auto w-full'
			style={{ height }}
			data-testid={testId}
		>
			<BarChart
				accessibilityLayer={false}
				layout='vertical'
				data={[row]}
				margin={{ top: 0, right: 8, bottom: 0, left: 8 }}
				barSize={30}
			>
				<XAxis
					type='number'
					domain={[0, total]}
					//ticks={getAxisTicks(total)}
					interval={0}
					tickCount={5}
					niceTicks='auto'
					tickLine //={false}
					fontSize={10}
					tickFormatter={(value) =>
						formatNumber(Number(value), NumberFormats.Compact)
					}
				/>
				<YAxis type='category' dataKey={() => ''} hide />
				<ChartTooltip
					shared={false}
					cursor={false}
					content={
						<ChartTooltipContent
							hideLabel
							formatter={(value, name) => {
								const segment = byKey[String(name)]

								return (
									<div className='flex w-full flex-col gap-1'>
										<div className='flex items-center gap-1.5 font-semibold'>
											<span
												className='size-2.5 shrink-0 rounded-[2px]'
												style={{ background: segment?.color }}
											/>
											{segment?.label ?? name}
										</div>
										<span>
											{t('widget.price_points_percent', {
												defaultValue: 'Price points percent',
											})}
											:{' '}
											{formatNumber(
												segment?.percent ?? 0,
												NumberFormats.Percent
											)}
										</span>
										<span>
											{t('widget.price_points', {
												defaultValue: 'Price points',
											})}
											: {formatNumber(Number(value))}
										</span>
									</div>
								)
							}}
						/>
					}
				/>
				{showLegend && (
					<ChartLegend
						content={
							<ChartLegendContent className='flex-wrap gap-x-3 gap-y-1' />
						}
					/>
				)}
				{segments.map(({ key, percent }) => (
					<Bar
						key={key}
						dataKey={key}
						stackId='segments'
						fill={`var(--color-${key})`}
					>
						<LabelList
							dataKey={key}
							content={(props) => (
								<SegmentLabel
									{...(props as object)}
									text={`${percent.toFixed(1)}%`}
								/>
							)}
						/>
					</Bar>
				))}
			</BarChart>
		</ChartContainer>
	)
}
